import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';

import meta from '../../settings/meta';
import sounds from '../../settings/sounds';
import { SoundsProvider } from '../SoundsProvider';
import { LanguageProvider } from '../LanguageProvider';
import { LanguageContext } from '../LanguageContext';

const WIDGET_SRC = process.env.GATSBY_CHATBOT_WIDGET_URL || 'http://172.17.239.197:5173/widget.js';
const WIDGET_ID = 'tfai-desk-widget';
const SHIM_ID = 'tfai-process-shim';
const API_BASE = process.env.GATSBY_CHATBOT_API_BASE || 'http://172.17.239.197:8800/api';

const Component = ({ children }) => {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const { protocol, hostname } = window.location || {};
    const isHttp = protocol === 'http:';
    const isLocal = ['localhost', '127.0.0.1'].includes(hostname) || /^172\./.test(hostname || '');
    const widgetIsHttps = /^https:\/\//i.test(WIDGET_SRC);
    const allowed = isHttp || isLocal || (protocol === 'https:' && widgetIsHttps);
    if (!allowed) return;

    let bridgeIntervalId = null;
    let bridgeIntervalTimeoutId = null;

    // Ensure stable session id across routes
    const storageKey = 'tfai:desk:sessionId';
    let sessionId = null;
    try {
      sessionId = window.localStorage.getItem(storageKey);
      if (!sessionId) {
        sessionId = 'tfai_' + Math.random().toString(36).slice(2) + '_' + Date.now().toString(36);
        window.localStorage.setItem(storageKey, sessionId);
      }
    } catch (e) {
      sessionId = window.__TFAI_DESK_SESSION_ID || ('tfai_' + Math.random().toString(36).slice(2));
    }
    window.__TFAI_DESK_SESSION_ID = sessionId;

    const ensureSessionId = () => {
      if (window.__TFAI_DESK_SESSION_ID) {
        return window.__TFAI_DESK_SESSION_ID;
      }
      let fallbackId = null;
      try {
        fallbackId = window.localStorage.getItem(storageKey);
      } catch (e) {}
      if (!fallbackId) {
        fallbackId = 'tfai_' + Math.random().toString(36).slice(2) + '_' + Date.now().toString(36);
        try {
          window.localStorage.setItem(storageKey, fallbackId);
        } catch (e) {}
      }
      window.__TFAI_DESK_SESSION_ID = fallbackId;
      return fallbackId;
    };

    const findDeskHostNode = () => {
      const hostSel = ':not(script)[data-texas-fortress-desk-root], :not(script)[data-texas-fortress-desk-host], :not(script)[data-texas-fortress-desk]';
      const host = document.querySelector(hostSel);
      if (host) return host;
      const frames = Array.from(document.querySelectorAll('iframe'));
      const fixedRight = frames.find(frame => {
        try {
          const cs = window.getComputedStyle(frame);
          return cs && cs.position === 'fixed' && (cs.right === '0px' || parseInt(cs.right || '0', 10) >= 0);
        } catch (e) {
          return false;
        }
      });
      if (fixedRight) return fixedRight;
      const scriptEl = document.getElementById(WIDGET_ID);
      return (scriptEl && scriptEl.nextElementSibling) ? scriptEl.nextElementSibling : null;
    };

    let deskBridgeRegistered = false;
    let pendingFitCall = null;
    let pendingFocusRequest = false;
    let focusLoopId = null;
    let processingPending = false;
    const ensureDeskBridge = () => {
      if (deskBridgeRegistered) return true;
      const node = findDeskHostNode();
      if (!node) return false;

      const openWidget = () => {
        const el = findDeskHostNode();
        if (!el) return false;
        try {
          el.style.display = 'block';
          el.style.opacity = '1';
          el.style.pointerEvents = 'auto';
          el.style.visibility = 'visible';
          // Try clicking toggle button inside widget if present
          const toggleButton = el.querySelector('button, [data-desk-toggle], [aria-label="Open chat"], [aria-label="Open"]');
          if (toggleButton) {
            toggleButton.click();
          }
        } catch (e) {}
        return true;
      };

      const postMessageToIframe = (type, payload) => {
        const iframe = document.querySelector('iframe[src*="desk"], iframe[src*="widget"]');
        if (!iframe || !iframe.contentWindow) return false;
        try {
          iframe.contentWindow.postMessage({ type, payload }, '*');
          return true;
        } catch (e) {
          return false;
        }
      };

      const focusIframeInput = () => {
        const iframe = document.querySelector('iframe[src*="desk"], iframe[src*="widget"]');
        if (!iframe || !iframe.contentWindow || !iframe.contentDocument) return false;
        try {
          if (iframe.contentWindow && iframe.contentWindow.focus) {
            iframe.contentWindow.focus();
          }
          const doc = iframe.contentDocument || iframe.contentWindow.document;
          const input = doc.querySelector('textarea, [contenteditable="true"], input[type="text"], input[type="search"]');
          if (input) {
            input.focus();
            if (input.click) input.click();
            if (input.select) input.select();
            return true;
          }
        } catch (e) {}
        return false;
      };

      const synthesizeInputSend = text => {
        const iframe = document.querySelector('iframe[src*="desk"], iframe[src*="widget"]');
        if (!iframe || !iframe.contentWindow || !iframe.contentDocument) return false;
        let success = false;
        try {
          const doc = iframe.contentDocument || iframe.contentWindow.document;
          const input = doc.querySelector('textarea, [contenteditable="true"], input[type="text"]');
          const form = input ? input.closest('form') : null;
          if (input) {
            input.focus();
            if ('value' in input) {
              input.value = text;
              input.dispatchEvent(new Event('input', { bubbles: true }));
            } else {
              input.textContent = text;
            }
            const enterEvent = new KeyboardEvent('keydown', { key: 'Enter', keyCode: 13, which: 13, bubbles: true });
            input.dispatchEvent(enterEvent);
            input.dispatchEvent(new KeyboardEvent('keyup', { key: 'Enter', keyCode: 13, which: 13, bubbles: true }));
            success = true;
          } else if (form) {
            const hiddenInput = document.createElement('input');
            hiddenInput.type = 'text';
            hiddenInput.value = text;
            form.appendChild(hiddenInput);
            form.dispatchEvent(new Event('submit', { bubbles: true }));
            hiddenInput.remove();
            success = true;
          }
        } catch (e) {}
        return success;
      };

      const focusInput = () => {
        const opened = openWidget();
        const sentViaPostMessage = postMessageToIframe('desk.client.focus', {});
        const focused = sentViaPostMessage ? true : focusIframeInput();
        if (!opened) openWidget();
        return sentViaPostMessage || focused || opened;
      };

      const startFocusLoop = (maxAttempts = 20, delay = 250) => {
        if (focusLoopId) return;
        let attempts = 0;
        focusLoopId = setInterval(() => {
          attempts += 1;
          const success = focusInput();
          if (success || attempts >= maxAttempts) {
            clearInterval(focusLoopId);
            focusLoopId = null;
            pendingFocusRequest = false;
          }
        }, delay);
      };

      const sendMessage = async text => {
        if (!text) return;
        const session = ensureSessionId();
        await fetch('/messages', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, sessionId: session })
        });
        postMessageToIframe('desk.message.sent', { text, sessionId: session });
        synthesizeInputSend(text);
      };

      const requestFitCall = async ({ text, fallback } = {}) => {
        if (!deskBridgeRegistered) {
          pendingFitCall = { text, fallback };
          kickstartDeskBridgeWatcher();
          observeDeskHost();
          return;
        }

        const message = text || 'Hi, I’d like to book a 15-minute FIT call.';
        const opened = openWidget();
        const sentViaPostMessage = postMessageToIframe('desk.client.send', { text: message });
        const sentViaInput = sentViaPostMessage ? true : synthesizeInputSend(message);
        if (!sentViaPostMessage && !sentViaInput) {
          try {
            await sendMessage(message);
          } catch (error) {
            console.error('[desk] Failed to send message', error);
            if (fallback) {
              window.location.href = fallback;
            }
            return;
          }
        }

        if (!opened && fallback) {
          window.location.href = fallback;
        }
      };

      window.tfaiDeskBridge = {
        openWidget,
        sendMessage,
        requestFitCall,
        focusInput,
        focusLoop: startFocusLoop,
        _debug: {
          postMessageToIframe,
          synthesizeInputSend
        }
      };
      deskBridgeRegistered = true;

      // Sync any pending focus flag set before bridge was ready
      if (typeof window !== 'undefined' && window.__TFAI_PENDING_FOCUS) {
        pendingFocusRequest = true;
        window.__TFAI_PENDING_FOCUS = false;
      }

      if (pendingFitCall && !processingPending) {
        processingPending = true;
        const queued = pendingFitCall;
        pendingFitCall = null;
        requestFitCall(queued).finally(() => {
          processingPending = false;
        });
      }
      if (pendingFocusRequest) {
        pendingFocusRequest = false;
        startFocusLoop();
      }
      return true;
    };

    const kickstartDeskBridgeWatcher = () => {
      if (bridgeIntervalId) return;
      bridgeIntervalId = setInterval(() => {
        if (ensureDeskBridge()) {
          clearInterval(bridgeIntervalId);
          bridgeIntervalId = null;
          if (bridgeIntervalTimeoutId) {
            clearTimeout(bridgeIntervalTimeoutId);
            bridgeIntervalTimeoutId = null;
          }
        }
      }, 250);
      bridgeIntervalTimeoutId = setTimeout(() => {
        if (bridgeIntervalId) {
          clearInterval(bridgeIntervalId);
          bridgeIntervalId = null;
        }
      }, 10000);
    };

    let hostObserver = null;
    const observeDeskHost = () => {
      if (hostObserver) return;
      hostObserver = new MutationObserver(() => {
        if (ensureDeskBridge()) {
          if (hostObserver) {
            hostObserver.disconnect();
            hostObserver = null;
          }
        }
      });
      hostObserver.observe(document.body, { childList: true, subtree: true });
    };

    // Inject process shim if missing (some bundles expect process.env)
    if (!document.getElementById(SHIM_ID)) {
      const shim = document.createElement('script');
      shim.id = SHIM_ID;
      shim.type = 'text/javascript';
      shim.text = "(function(){try{if(typeof window!=='undefined'&&typeof window.process==='undefined'){window.process={env:{NODE_ENV:'development'}};}else if(window&&window.process&&window.process.env&&!window.process.env.NODE_ENV){window.process.env.NODE_ENV='development';}}catch(e){}})();";
      document.head.appendChild(shim);
    }

    // Proxy widget network calls to the Parlant API in dev, even if the
    // widget accidentally calls relative paths on the host.
    try {
      const API = API_BASE.replace(/\/$/, '');
      const originalFetch = window.fetch ? window.fetch.bind(window) : null;
      const shouldRewrite = url => {
        try {
          const u = new URL(url, window.location.origin);
          const own = u.origin === window.location.origin;
          return own && (
            u.pathname === '/sessions' ||
            u.pathname === '/messages' ||
            u.pathname.startsWith('/api/') ||
            u.pathname === '/api' ||
            u.pathname.startsWith('/agents')
          );
        } catch (e) {
          // Relative or invalid URL, rewrite by default if starts with '/' and matches
          return /^\/(sessions|messages|api\b|agents)/.test(String(url || ''));
        }
      };
      const rewriteUrl = url => {
        try {
          const u = new URL(url, window.location.origin);
          const path = u.pathname.startsWith('/api') ? u.pathname.replace(/^\/api/, '') : u.pathname;
          return API + path + (u.search || '');
        } catch (e) {
          const path = String(url || '');
          const normalized = path.startsWith('/api') ? path.replace(/^\/api/, '') : path;
          return API + normalized;
        }
      };
      if (originalFetch && !window.__TFAI_FETCH_PATCHED__) {
        const isRequest = val => typeof Request !== 'undefined' && val instanceof Request;
        const mergeHeaders = (base, override) => {
          const merged = new Headers(base || {});
          if (override) {
            new Headers(override).forEach((v, k) => merged.set(k, v));
          }
          return merged;
        };
        const buildInitFromRequest = req => {
          if (!req) return {};
          const initFromReq = {
            method: req.method,
            headers: req.headers,
            // Only supply body for non-GET/HEAD
            body: req.method && !/^GET|HEAD$/i.test(req.method) ? req.body : undefined,
            mode: req.mode,
            credentials: req.credentials,
            cache: req.cache,
            redirect: req.redirect,
            referrer: req.referrer && req.referrer !== 'about:client' ? req.referrer : undefined,
            referrerPolicy: req.referrerPolicy,
            integrity: req.integrity,
            keepalive: req.keepalive,
            signal: req.signal
          };
          return initFromReq;
        };
        window.fetch = (input, init) => {
          const url = isRequest(input) ? input.url : input;
          if (url && shouldRewrite(url)) {
            const newUrl = rewriteUrl(url);

            if (isRequest(input)) {
              const reqInit = buildInitFromRequest(input);
              // Merge headers carefully to preserve originals and allow overrides
              const headers = mergeHeaders(reqInit.headers, init && init.headers);
              const merged = { ...reqInit, ...(init || {}), headers };
              // Default to CORS/omit only if not explicitly provided
              if (!('mode' in merged)) merged.mode = 'cors';
              if (!('credentials' in merged)) merged.credentials = 'omit';
              return originalFetch(newUrl, merged);
            }

            const merged = { ...(init || {}) };
            if (!('mode' in merged)) merged.mode = 'cors';
            if (!('credentials' in merged)) merged.credentials = 'omit';
            return originalFetch(newUrl, merged);
          }
          return originalFetch(input, init);
        };
        window.__TFAI_FETCH_PATCHED__ = true;
      }
      if (!window.__TFAI_XHR_PATCHED__ && window.XMLHttpRequest) {
        const origOpen = XMLHttpRequest.prototype.open;
        XMLHttpRequest.prototype.open = function (method, url, async, user, password) {
          try {
            if (url && shouldRewrite(url)) {
              url = rewriteUrl(url);
            }
          } catch (e) {}
          return origOpen.call(this, method, url, async, user, password);
        };
        window.__TFAI_XHR_PATCHED__ = true;
      }
    } catch (e) {
      // swallow
    }

    // Global config so the widget reads API base reliably (some builds prefer globals)
    try {
      window.__TFAI_DESK__ = {
        apiBase: API_BASE,
        sessionId,
        // Right-side dock, open by default
        position: 'right',
        initialOpen: true,
        // Hint to render as a full-height side panel (best-effort; widget may ignore)
        mode: 'panel',
        fullHeight: true,
        expandOnLoad: true,
        dock: 'right',
        drawerWidth: 420
      };
    } catch (e) {}

    const updatePageClass = () => {
      try {
        const path = window.location && window.location.pathname ? window.location.pathname : '';
        if (path.startsWith('/music')) {
          document.body.classList.add('tfai-page-music');
        } else {
          document.body.classList.remove('tfai-page-music');
        }
      } catch (e) {}
    };

    updatePageClass();
    window.addEventListener('route-change', updatePageClass);

    // Inject widget once
    if (!document.getElementById(WIDGET_ID)) {
      const s = document.createElement('script');
      s.id = WIDGET_ID;
      s.src = WIDGET_SRC;
      s.defer = true;
      s.type = 'text/javascript';
      s.setAttribute('data-texas-fortress-desk', '');
      s.setAttribute('data-api-base', API_BASE);
      s.setAttribute('data-api', API_BASE);
      s.setAttribute('data-position', 'right');
      s.setAttribute('data-initial-open', 'true');
      s.setAttribute('data-session-id', sessionId);
      // Best-effort sizing hints for full-height right panel
      s.setAttribute('data-mode', 'panel');
      s.setAttribute('data-full-height', 'true');
      s.setAttribute('data-open', 'true');
      s.setAttribute('data-dock', 'right');
      s.setAttribute('data-drawer-width', '420');
      document.body.appendChild(s);

      kickstartDeskBridgeWatcher();

      // Music page: keep widget out of the way of Webamp
      try {
        let style = document.getElementById('tfai-desk-music-overrides');
        if (!style) {
          style = document.createElement('style');
          style.id = 'tfai-desk-music-overrides';
          style.type = 'text/css';
          style.textContent = [
            'body.tfai-page-music iframe[src*="desk"],',
            'body.tfai-page-music iframe[src*="widget"],',
            'body.tfai-page-music [data-texas-fortress-desk-root],',
            'body.tfai-page-music [data-texas-fortress-desk-host],',
            'body.tfai-page-music [data-texas-fortress-desk]',
            '{',
            '  display: none !important;',
            '}'
          ].join(' ');
          document.head.appendChild(style);
        }
      } catch (e) {}

      // Fallback: if the widget ignores attributes, aggressively coerce layout via styles.
      // 1) Add a broad CSS override for right-docked, tall iframe widgets
      // 2) Find the actual host/iframe element and apply inline !important rules
      // 3) Keep re-applying using a MutationObserver if the widget tries to reset itself
      const ensureOverrideStyle = () => {
        try {
          let style = document.getElementById('tfai-desk-overrides');
          if (!style) {
            style = document.createElement('style');
            style.id = 'tfai-desk-overrides';
            style.type = 'text/css';
            style.textContent = [
              'iframe[src*="desk"], iframe[src*="widget"],',
              '[data-texas-fortress-desk-root], [data-texas-fortress-desk-host]',
              '{ position: fixed !important; right: 0 !important; bottom: 0 !important; z-index: 2147483646 !important; max-height: 95vh !important; }'
            ].join('');
            document.head.appendChild(style);
          }
        } catch (e) {}
      };

      const findWidgetNode = () => {
        const hostSel = ':not(script)[data-texas-fortress-desk-root], :not(script)[data-texas-fortress-desk-host], :not(script)[data-texas-fortress-desk]';
        const host = document.querySelector(hostSel);
        if (host) return host;
        const iframes = Array.from(document.querySelectorAll('iframe'));
        const fixedRight = iframes.find(f => {
          try {
            const cs = window.getComputedStyle(f);
            return cs && cs.position === 'fixed' && (cs.right === '0px' || parseInt(cs.right, 10) >= 0);
          } catch (e) { return false; }
        });
        if (fixedRight) return fixedRight;
        const scriptEl = document.getElementById(WIDGET_ID);
        return (scriptEl && scriptEl.nextElementSibling) ? scriptEl.nextElementSibling : null;
      };

      const applyInlineSizing = (el) => {
        if (!el) return false;
        const rect = typeof el.getBoundingClientRect === 'function' ? el.getBoundingClientRect() : { height: 0 };
        const vh = window.innerHeight || (document.documentElement && document.documentElement.clientHeight) || 0;
        const current = rect.height || 0;
        // Double current height, clamp to viewport
        const target = Math.max(0, Math.min(current * 2 || (vh * 0.9), Math.max(0, vh - 10)));

        try {
          el.style.setProperty('position', 'fixed', 'important');
          el.style.setProperty('right', '0', 'important');
          el.style.setProperty('bottom', '0', 'important');
          el.style.setProperty('z-index', '2147483646', 'important');
          el.style.setProperty('height', target ? `${target}px` : `${Math.floor(vh * 0.9)}px`, 'important');
          el.style.setProperty('max-height', `${Math.floor(vh * 0.95)}vh`, 'important');
        } catch (e) { return false; }

        // Verify the change took effect (tolerate small differences)
        const after = el.getBoundingClientRect ? el.getBoundingClientRect().height : 0;
        return after && Math.abs(after - target) < 8;
      };

      // eslint-disable-next-line no-unused-vars
      const coerceFullHeight = () => {
        const ENABLE_OVERRIDE = false;
        if (!ENABLE_OVERRIDE) return false;
        try {
          ensureOverrideStyle();
          // Try known/likely node first
          let ok = false;
          const node = findWidgetNode();
          if (node) ok = applyInlineSizing(node);

          // Fallback: scan for bottom-right fixed candidates and coerce them
          if (!ok) {
            const all = Array.from(document.body.querySelectorAll('*'));
            // eslint-disable-next-line no-unused-vars
            const vh = window.innerHeight || (document.documentElement && document.documentElement.clientHeight) || 0;
            const candidates = all
              .filter(el => {
                try {
                  const cs = window.getComputedStyle(el);
                  if (!cs || cs.position !== 'fixed') return false;
                  const right = parseInt(cs.right || '0', 10);
                  const bottom = parseInt(cs.bottom || '0', 10);
                  const w = parseInt(cs.width || '0', 10);
                  const h = parseInt(cs.height || '0', 10);
                  if (!isFinite(right) || !isFinite(bottom)) return false;
                  // Near bottom-right and has non-trivial size
                  return right >= 0 && right <= 32 && bottom >= 0 && bottom <= 32 && w >= 200 && h >= 120;
                } catch (e) { return false; }
              })
              // Sort by z-index descending to pick top-most overlay
              .sort((a, b) => {
                const za = parseInt(window.getComputedStyle(a).zIndex || '0', 10) || 0;
                const zb = parseInt(window.getComputedStyle(b).zIndex || '0', 10) || 0;
                return zb - za;
              });
            for (const el of candidates) {
              const applied = applyInlineSizing(el);
              if (applied) { ok = true; break; }
            }
          }
          // Aggressive: observe and re-apply if mutated
          if (!window.__TFAI_DESK_OBSERVER__) {
            try {
              const obs = new MutationObserver(() => {
                const n = findWidgetNode();
                if (n) applyInlineSizing(n);
                // Also re-coerce any bottom-right fixed candidates
                const all = Array.from(document.body.querySelectorAll('*'));
                const candidates = all.filter(el => {
                  try {
                    const cs = window.getComputedStyle(el);
                    if (!cs || cs.position !== 'fixed') return false;
                    const right = parseInt(cs.right || '0', 10);
                    const bottom = parseInt(cs.bottom || '0', 10);
                    const w = parseInt(cs.width || '0', 10);
                    const h = parseInt(cs.height || '0', 10);
                    return right >= 0 && right <= 32 && bottom >= 0 && bottom <= 32 && w >= 200 && h >= 120;
                  } catch (e) { return false; }
                });
                for (const el of candidates) applyInlineSizing(el);
              });
              obs.observe(document.body, { subtree: true, attributes: true, childList: true });
              window.__TFAI_DESK_OBSERVER__ = obs;
            } catch (e) {}
          }
          return ok;
        } catch (e) {
          return false;
        }
      };

      // Try for a short window after load in case the widget mounts async
      // Disabled unless ENABLE_OVERRIDE is set above
      // let attempts = 0;
      // const timer = setInterval(() => {
      //   attempts++;
      //   const ok = coerceFullHeight();
      //   if (ok || attempts > 50) {
      //     clearInterval(timer);
      //   }
      // }, 100);
    }

    const queueFitCall = payload => {
      pendingFitCall = payload || {};
      kickstartDeskBridgeWatcher();
      observeDeskHost();
    };

    if (typeof window.tfaiDeskBridge === 'undefined') {
      window.tfaiDeskBridge = {
        requestFitCall: queueFitCall
      };
    }

    kickstartDeskBridgeWatcher();
    ensureDeskBridge();
    observeDeskHost();

    return () => {
      if (bridgeIntervalId) {
        clearInterval(bridgeIntervalId);
      }
      if (bridgeIntervalTimeoutId) {
        clearTimeout(bridgeIntervalTimeoutId);
      }
      if (hostObserver) {
        hostObserver.disconnect();
      }
      window.removeEventListener('route-change', updatePageClass);
    };
  }, []);

  return (
    <LanguageProvider>
      <LanguageContext.Consumer>
        {({ language, t }) => {
          const pageTitle = (t && t('layout.title')) || meta.title;
          const description = (t && t('layout.description')) || meta.description;

          return (
            <>
              <Helmet>
                <html lang={language || 'en'} />

                <meta name='viewport' content='width=device-width, initial-scale=1' />
                <meta httpEquiv='X-UA-Compatible' content='IE=edge,chrome=1' />

                <meta name='robots' content='index, follow' />
                <title>{pageTitle}</title>
                <meta name='description' content={description} />
                <meta name='keywords' content={meta.keywords} />

                <meta property='og:title' content={pageTitle} />
                <meta property='og:site_name' content={pageTitle} />
                <meta property='og:description' content={description} />
                <meta property='og:type' content='website' />
                <meta property='og:url' content={meta.url} />
                <meta property='og:image' content={meta.seoImage} />

                <meta name='twitter:card' content='summary_large_image' />
                <meta name='twitter:title' content={pageTitle} />
                <meta name='twitter:description' content={description} />
                <meta name='twitter:site' content={meta.twitter} />
                <meta name='twitter:creator' content={meta.twitter} />
                <meta name='twitter:image' content={meta.seoImage} />

                <meta name='mobile-web-app-capable' content='yes' />
                <meta name='theme-color' content={meta.color} />
                <meta name='apple-mobile-web-app-capable' content='yes' />
                <meta name='apple-mobile-web-app-title' content={pageTitle} />
                <meta name='apple-mobile-web-app-status-bar-style' content={meta.color} />
                <meta name='msapplication-TileColor' content={meta.color} />

                <link
                  rel='stylesheet'
                  href='https://fonts.googleapis.com/css2?family=Bungee&family=Orbitron:wght@400;500;700&family=Electrolize&display=swap'
                />
                <link
                  rel='stylesheet'
                  href='//cdn.materialdesignicons.com/3.0.39/css/materialdesignicons.min.css'
                />
              </Helmet>
              <SoundsProvider sounds={sounds}>
                {children}
              </SoundsProvider>
            </>
          );
        }}
      </LanguageContext.Consumer>
    </LanguageProvider>
  );
};

Component.displayName = 'Layout';

Component.propTypes = {
  children: PropTypes.node.isRequired
};

export { Component };
